import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { Keyboard } from 'react-native';
import {
    BodyLargeRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Input,
    KeyboardCompatibleBottomSheetTextInput,
    LabelRegular,
    Sheet,
    XStack,
    YStack,
} from 'smoothie';

import { MONEY_MAP_NAME_MAX_LENGTH } from '../constants';
import {
    addRuleGroupInMoneyMap,
    editableMoneyMapAtom,
} from '../state/editableMoneyMapAtom';

interface MoneyMapAddRuleSetSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

export const MoneyMapAddRuleSetSheet = ({
    isOpen,
    onClose,
}: MoneyMapAddRuleSetSheetProps) => {
    const [editableMoneyMap, setEditableMoneyMap] =
        useAtom(editableMoneyMapAtom);
    const [name, setName] = useState('');

    useEffect(() => {
        if (isOpen) {
            setName('');
        }
    }, [isOpen]);

    const handleSave = () => {
        const trimmedName = name.trim();
        if (editableMoneyMap == null || trimmedName.length === 0) return;

        const priority = editableMoneyMap.moneyMapRuleGroups.reduce(
            (max, group) => Math.max(max, group.priority),
            0,
        );

        setEditableMoneyMap(
            addRuleGroupInMoneyMap(editableMoneyMap, {
                id: `new-goal-set-${Date.now()}`,
                moneyMapId: editableMoneyMap.id,
                name: trimmedName,
                priority: priority + 1,
                percentage: 1,
                createdAt: new Date().toISOString(),
                updatedAt: null,
            }),
        );
        onClose();
    };

    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={false}>
            <YStack $sm={{ minWidth: 400 }} gap={24}>
                <BodyLargeRegular textAlign="center">
                    Create new rule set
                </BodyLargeRegular>

                <YStack gap={8}>
                    <LabelRegular marginLeft={8} color="$gray1100">
                        Goal set name
                    </LabelRegular>
                    <KeyboardCompatibleBottomSheetTextInput
                        component={Input}
                        value={name}
                        onChangeText={setName}
                        maxLength={MONEY_MAP_NAME_MAX_LENGTH}
                        placeholder="Enter goal set name"
                        returnKeyType="done"
                        onSubmitEditing={() => {
                            Keyboard.dismiss();
                        }}
                        fontWeight="regular"
                    />
                </YStack>

                <XStack gap={12}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={onClose}
                        buttonProps={{ flexShrink: 1 }}
                    />
                    <Button
                        disabled={name.trim().length === 0}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSave}
                        buttonProps={{ flexShrink: 1 }}
                    />
                </XStack>
            </YStack>
        </Sheet>
    );
};
